import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { AuthError, useAuthServices } from '../authServices';
import { AuthLayout } from '../AuthLayout';
import { Icon } from '../../common/Icon';

type Step = 'email' | 'reset';

/** QZ-D28: two-step password reset, both steps calling Cognito directly. */
export const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const { cognito } = useAuthServices();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const errorMessage = (thrown: unknown) =>
    thrown instanceof AuthError ? t(`auth.error.${thrown.code}`) : t('auth.error.Unknown');

  const handleSendCode = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await cognito.forgotPassword(email);
      setStep('reset');
    } catch (thrown) {
      setError(errorMessage(thrown));
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await cognito.confirmForgotPassword(email, code, newPassword);
      navigate('/signin', { state: { passwordReset: true } });
    } catch (thrown) {
      setError(errorMessage(thrown));
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      footer={
        <Link to="/signin">
          <Icon name="arrow-left" /> {t('auth.callback.backToSignIn')}
        </Link>
      }
    >
      <h1>{t('auth.forgot.heading')}</h1>
      <p className="qz-sub">
        {step === 'email' ? t('auth.forgot.intro') : t('auth.forgot.sentTo', { email })}
      </p>
      {error && (
        <div className="alert alert-danger">
          <Icon name="circle-exclamation" />
          <div>{error}</div>
        </div>
      )}
      {step === 'email' && (
        <form onSubmit={handleSendCode}>
          <div className="mb-3">
            <label htmlFor="forgot-email" className="form-label">
              {t('auth.email')}
            </label>
            <input
              id="forgot-email"
              type="email"
              autoComplete="username"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary w-100" disabled={busy}>
            {t('auth.forgot.sendCode')}
          </button>
        </form>
      )}
      {step === 'reset' && (
        <form onSubmit={handleReset}>
          <div className="mb-3">
            <label htmlFor="forgot-code" className="form-label">
              {t('auth.code')}
            </label>
            <input
              id="forgot-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="form-control qz-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="forgot-new-password" className="form-label">
              {t('auth.newPassword')}
            </label>
            <input
              id="forgot-new-password"
              type="password"
              autoComplete="new-password"
              className="form-control"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary w-100" disabled={busy}>
            {t('auth.forgot.submit')}
          </button>
        </form>
      )}
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
